import {useId} from 'react'
import type {LucideIcon} from 'lucide-react'
import {Settings, Users, ScrollText, Server, KeyRound, Cog, BarChart3, Send, ClipboardList, MessageSquareText, Database, Plug} from 'lucide-react'
import {CardHeader, CardTitle, CardDescription} from '@/components/ui/card'
import {ClickableCard} from '@/components/shared/ClickableCard'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {PageLayout} from '@/components/shared/PageLayout'
import {IconCircle} from '@/components/shared/IconCircle'
import {InlineRow} from '@/components/shared/InlineRow'
import {CONCEPT_ICON} from '@/lib/concepts'
import {DashboardCardId, DashboardTestIds} from '@/testids/DashboardTestIds'

interface DashboardCardProps {
    testId: DashboardCardId
    title: string
    description: string
    icon: LucideIcon
    href: string
}

function DashboardCard({testId, title, description, icon, href}: DashboardCardProps) {
    const titleId = useId()
    const descriptionId = useId()
    return (
        <ClickableCard
            to={href}
            className="min-h-[120px]"
            data-testid={DashboardTestIds.dashboardCard(testId)}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
        >
            <CardHeader>
                <InlineRow gap="lg">
                    <IconCircle icon={icon} variant="primary" size="md"/>
                    <div>
                        <CardTitle id={titleId} className="text-lg" data-testid={DashboardTestIds.dashboardCardTitle(testId)}>{title}</CardTitle>
                        <CardDescription id={descriptionId}>{description}</CardDescription>
                    </div>
                </InlineRow>
            </CardHeader>
        </ClickableCard>
    )
}

export function Dashboard() {
    const i18n = useI18n()
    const {user, hasAnyPermission, hasPermission} = useAuth()

    const showAdminCard = hasAnyPermission([permissions.roles.view, permissions.roleAssignments.view])
    const showConnectorsCard = hasAnyPermission([permissions.inboundConnectors.view, permissions.outboundConnectors.view])
    const showSchemaCard = hasAnyPermission([permissions.types.view, permissions.attributeDefinitions.view, permissions.formLayouts.manage, permissions.partyRelationshipSchema.view, permissions.valueLists.view])
    const showIdentityManagementCard = hasAnyPermission([permissions.parties.view, permissions.identities.view, permissions.accounts.view])
    const showSystemManagementCard = hasAnyPermission([permissions.systems.view, permissions.resources.view, permissions.enforcementObjects.view])
    const showAccessGovernanceCard = hasAnyPermission([permissions.assignments.revoke, permissions.accessProfiles.view, permissions.accessRequests.view, permissions.certifications.review])
    const showAnalyticsCard = hasAnyPermission([permissions.identities.view, permissions.systems.view])
    const showProvisioningCard = hasPermission(permissions.provisioning.view)
    const showAuditCard = hasPermission(permissions.auditLogs.view)
    const showBackgroundJobsCard = hasPermission(permissions.jobs.view)
    const showReportingCard = hasPermission(permissions.reporting.query)

    return (
        <PageLayout
            variant="dashboard"
            icon={CONCEPT_ICON.dashboard}
            title={String(i18n.dashboard.title)}
            description={String(i18n.dashboard.subtitle)}
            data-testid="dashboard-page"
        >
            <nav aria-label={String(i18n.dashboard.sectionsNav)}>
            <DetailGrid columns={3} className="gap-6">
                {user?.identityId && (
                    <DashboardCard
                        testId={DashboardCardId.MyIam}
                        title={i18n.dashboard.myIam}
                        description={i18n.dashboard.myIamDesc}
                        icon={ClipboardList}
                        href={routes.my.root()}
                    />
                )}

                {showAdminCard && (
                    <DashboardCard
                        testId={DashboardCardId.IamAdmin}
                        title={i18n.dashboard.iamAdmin}
                        description={i18n.dashboard.iamAdminDesc}
                        icon={Settings}
                        href={routes.admin.root()}
                    />
                )}

                {showSchemaCard && (
                    <DashboardCard
                        testId={DashboardCardId.Schema}
                        title={i18n.dashboard.schema}
                        description={i18n.dashboard.schemaDesc}
                        icon={Database}
                        href={routes.schema.root()}
                    />
                )}

                {showIdentityManagementCard && (
                    <DashboardCard
                        testId={DashboardCardId.IdentityManagement}
                        title={i18n.dashboard.identityManagement}
                        description={i18n.dashboard.identityManagementDesc}
                        icon={Users}
                        href={routes.identityManagement.root()}
                    />
                )}

                {showConnectorsCard && (
                    <DashboardCard
                        testId={DashboardCardId.Connectors}
                        title={i18n.dashboard.connectors}
                        description={i18n.dashboard.connectorsDesc}
                        icon={Plug}
                        href={routes.connectors.root()}
                    />
                )}

                {showSystemManagementCard && (
                    <DashboardCard
                        testId={DashboardCardId.TargetSystems}
                        title={i18n.dashboard.targetSystems}
                        description={i18n.dashboard.targetSystemsDesc}
                        icon={Server}
                        href={routes.systems.root()}
                    />
                )}

                {showAccessGovernanceCard && (
                    <DashboardCard
                        testId={DashboardCardId.AccessGovernance}
                        title={i18n.dashboard.accessGovernance}
                        description={i18n.dashboard.accessGovernanceDesc}
                        icon={KeyRound}
                        href={routes.accessGovernance.root()}
                    />
                )}

                {showAnalyticsCard && (
                    <DashboardCard
                        testId={DashboardCardId.EffectiveAccess}
                        title={i18n.dashboard.effectiveAccess}
                        description={i18n.dashboard.effectiveAccessDesc}
                        icon={BarChart3}
                        href={routes.effectiveAccess.root()}
                    />
                )}

                {showProvisioningCard && (
                    <DashboardCard
                        testId={DashboardCardId.AccessProvisioning}
                        title={i18n.dashboard.accessProvisioning}
                        description={i18n.dashboard.accessProvisioningDesc}
                        icon={Send}
                        href={routes.provisioning.dashboard.path()}
                    />
                )}

                {showBackgroundJobsCard && (
                    <DashboardCard
                        testId={DashboardCardId.BackgroundJobs}
                        title={i18n.dashboard.backgroundJobs}
                        description={i18n.dashboard.backgroundJobsDesc}
                        icon={Cog}
                        href={routes.backgroundJobs.root()}
                    />
                )}

                {showReportingCard && (
                    <DashboardCard
                        testId={DashboardCardId.Reporting}
                        title={i18n.dashboard.reporting}
                        description={i18n.dashboard.reportingDesc}
                        icon={MessageSquareText}
                        href={routes.reporting.root()}
                    />
                )}

                {showAuditCard && (
                    <DashboardCard
                        testId={DashboardCardId.AuditLog}
                        title={i18n.dashboard.auditLog}
                        description={i18n.dashboard.auditLogDesc}
                        icon={ScrollText}
                        href={routes.auditLog.path()}
                    />
                )}
            </DetailGrid>
            </nav>
        </PageLayout>
    )
}
